import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../../../../../../components/Generic/Modal";
import { Location } from "../../../../../../../../../../models/location";
import { Filters, Searches } from "../../../../../../../../../../models/shipeezi.table";
import { AddressTypeKeys } from "../../../../../../../../../../util/enums";
import { useDespatchContext } from "../../../../../../context/DespatchContext";
import useAction from "../../../../../hooks/useAction";
import useStyles from "./styles";

interface ModalProps {
  hasError?: boolean;
  type: "delivery" | "pickUp";
}

export interface AddExistingAddressModalHandles {
  open: () => void;
  close: () => void;
}

const AddExistingAddressModal: ForwardRefRenderFunction<
  AddExistingAddressModalHandles,
  ModalProps
> = (props, ref) => {
  const { hasError, type } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { handleAddAddressList } = useDespatchContext();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const { getAllLocations, countLocations } = useAction();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(6);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [totalItems, setTotalItems] = useState<number>(100);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const { data, isLoading, isRefetching } = getAllLocations(page, pageSize, searches, filters);
  const { data: totalCount } = countLocations(searches, filters);

  useEffect(() => {
    if (data) {
      setLocations(data);
    }
  }, [data]);

  useEffect(() => {
    if (totalCount) {
      setTotalItems(totalCount);
    }
  }, [totalCount]);

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "addressLine1",
      title: `${translate("address1")}`,
    },
    {
      name: "addressLine2",
      title: `${translate("address2")}`,
      hideSearch: true,
    },
    {
      name: "suburb",
      title: `${translate("suburb")}`,
    },
    {
      name: "state",
      title: `${translate("state")}`,
    },
    {
      name: "postalcode",
      title: `${translate("postalCode")}`,
    },
    {
      name: "country",
      title: `${translate("country")}`,
    },
  ];

  const open = useCallback(() => {
    setOpenModal(true);
  }, []);
  const close = useCallback(() => {
    setOpenModal(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleClose = () => {
    close();
  };

  const handleSubmit = () => {
    const key =
      type === "delivery" ? AddressTypeKeys.DESTINATION_ADDRESS : AddressTypeKeys.ORIGIN_ADDRESS;
    const locationsToAdd = locations.filter(
      (location) => location?.id && selectedItems.includes(location?.id)
    );

    handleAddAddressList(key, locationsToAdd);
    handleClose();
  };

  const handleChangeCurrentPage = (currentPage: any) => {
    setPage(currentPage - 1);
  };

  const handleChangeRowsPerPage = async (newPageSize: any) => {
    setPageSize(newPageSize);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleNewRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      onChange: handleFilter,
    },
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      close();
    }
  }, [hasError]);

  const modalTitle = useMemo(() => translate(`${type}Address`), [type]);

  return (
    <Modal
      open={openModal}
      handleClose={handleClose}
      title={modalTitle}
      disableEnforceFocus
      data-cy={modalTitle}
      dataCyTitle={modalTitle}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => handleClose(),
        dataCySave: "submitChangesButton",
        textSave: translate("addAddresses"),
        loading: false,
        saveDisabled: !selectedItems?.length,
        onSave: () => handleSubmit(),
      }}
    >
      <section className={classes.modalSection}>
        <ShipeeziNewGrid
          data={locations}
          totalItems={totalItems}
          loadingData={isLoading || isRefetching}
          namePage={translate("despatches")}
          emptyData={
            isLoading ? translate("loadingLocations") : translate("noInformationToBeShown")
          }
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [6, 20, 40, 60, 100] : []}
          onChangePageSize={handleChangeRowsPerPage}
          onNextPage={handleChangeCurrentPage}
          onPreviousPage={handleChangeCurrentPage}
          onGoToPage={handleChangeCurrentPage}
          generalActions={generalActions}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showSelection={totalItems !== 0}
          showFooterGrid={totalItems !== 0}
        />
      </section>
    </Modal>
  );
};

export default forwardRef(AddExistingAddressModal);
